import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { OAuthConnection, scopeLabel } from "../Login/oauthTypes";

import { buttonClass } from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Panel from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { Appear } from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
const fmt = (iso: string | null) =>
	iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

/**
 * Bots signed in as a person. Without `userId` it lists and disconnects the
 * caller's own; with `userId` it is the admin view of that user's (can_admin).
 */
const ConnectedBots = ({ userId }: { userId?: string }) => {
	const api = useApi();
	const toast = useToast();
	const admin = !!userId;
	const [rows, setRows] = useState<OAuthConnection[] | null>(null);
	const [failed, setFailed] = useState(false);
	const [busy, setBusy] = useState<string | null>(null);

	const load = useCallback(async () => {
		try {
			const path = admin ? `/oauth/admin/connections?user_id=${encodeURIComponent(userId!)}` : "/oauth/connections";
			setRows((await api.get(path, { silent: true })) || []);
			setFailed(false);
		} catch {
			setFailed(true);
		}
	}, [userId]);

	useEffect(() => {
		setRows(null);
		load();
	}, [load]);

	const remove = async (row: OAuthConnection) => {
		setBusy(row.family_id);
		try {
			await api.delete(
				admin ? `/oauth/admin/connections/${row.family_id}` : `/oauth/connections/${row.family_id}`,
				{ silent: true, skipJsonParse: true }
			);
			setRows((prev) => (prev || []).filter((r) => r.family_id !== row.family_id));
			toast.success(admin ? `${row.client_name} revoked` : `${row.client_name} disconnected`);
		} catch (err) {
			toast.error((err as Error)?.message || "Could not disconnect. Try again.");
		} finally {
			setBusy(null);
		}
	};

	const revokeAll = async () => {
		if (!window.confirm("Revoke every bot connection for this user?")) return;
		setBusy("all");
		try {
			await api.post(`/oauth/admin/users/${userId}/revoke-all`, { silent: true });
			setRows([]);
			toast.success("All bot connections revoked");
		} catch (err) {
			toast.error((err as Error)?.message || "Could not revoke. Try again.");
		} finally {
			setBusy(null);
		}
	};

	const verb = admin ? "Revoke" : "Disconnect";

	return (
		<Panel
			title="Connected bots"
			level={3}
			actions={admin ? (
				<Appear show={!!rows && rows.length > 0} as="div" className="shrink-0">
					<button
						type="button"
						onClick={revokeAll}
						disabled={busy !== null}
						className={`${buttonClass('danger', 'sm')} shrink-0`}
					>
						{busy === "all" ? "Revoking…" : "Revoke all"}
					</button>
				</Appear>
			) : undefined}
		>
			<FadeIn key={failed ? "failed" : rows === null ? "loading" : rows.length === 0 ? "empty" : "rows"}>
			{failed ? (
				<Alert onRetry={load}>Could not load connections.</Alert>
			) : rows === null ? (
				<SkeletonGroup label="Loading connections" className="space-y-1.5">
					<Skeleton className="h-12 w-full" />
				</SkeletonGroup>
			) : rows.length === 0 ? (
				<EmptyState variant="line">
					{admin
						? "No bots are connected as this user."
						: "No bots connected. When a bot asks to sign in as you, it shows up here."}
				</EmptyState>
			) : (
				<ListStack
					items={rows}
					getKey={(row) => row.family_id}
					on="panel"
					spacing="loose"
					label="Connected bots"
					renderRow={(row) => (
						<ListRow
							actions={
								<button
									type="button"
									onClick={() => remove(row)}
									disabled={busy !== null}
									className={`${buttonClass('danger-quiet', 'sm')} shrink-0`}
								>
									{busy === row.family_id ? "Working…" : verb}
								</button>
							}
						>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-medium text-on-surface">{row.client_name}</div>
								<div className="truncate text-xs text-on-surface-variant">
									{row.scopes.map(scopeLabel).join(", ")}
								</div>
								<div className="text-xs tabular-nums text-on-surface-variant">
									Connected {fmt(row.issued_at)}
									{row.last_used_at ? `, last used ${fmt(row.last_used_at)}` : ", not used yet"}
								</div>
							</div>
						</ListRow>
					)}
				/>
			)}
			</FadeIn>
		</Panel>
	);
};

export default ConnectedBots;
